import { useState, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  type SortingState,
  type ColumnDef,
  flexRender,
} from "@tanstack/react-table";
import {
  Plus,
  Search,
  Cpu,
  Pencil,
  Table2,
  Columns3,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useTranslation } from "@/i18n/use-translation";
import type { TFunction } from "@/i18n/catalog";
import { apiError, showApiError } from "@/lib/api-error";
import { formatDate } from "@/lib/format";
import { useAdminOrgStore } from "@/stores/admin-org";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { KanbanBoard } from "@/components/kanban-board";
import {
  configOptions,
  DEVICE_LOCATION_OPTIONS,
  DEVICE_LOCATION_VALUES,
  optionLabel,
} from "@/lib/config-options";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";
const PAGE_SIZE = 10;

interface Device {
  id: string;
  kitchen_id: string;
  device_serial: string;
  name: string | null;
  status: string;
  location: string | null;
  created_at: string;
}

interface Kitchen {
  id: string;
  name: string;
  site_id: string;
}

interface Site {
  id: string;
  name: string;
  account_id: string;
}

interface Account {
  id: string;
  name: string;
}

/** Built inside the component and memoised on `t`, so the messages are the reader's — a
 * module-level schema would freeze whichever locale happened to be active at import
 * (POS2-1133 pinned decision, the `makeSchema(t)` factory CP3a landed). */
function makeCreateDeviceSchema(t: TFunction) {
  return z.object({
    kitchen_id: z.string().min(1, t("validation.kitchenRequired")),
    device_serial: z.string().min(1, t("validation.serialRequired")),
    name: z.string().optional(),
  });
}

type CreateDeviceForm = z.infer<ReturnType<typeof makeCreateDeviceSchema>>;

const STATUS_STYLES: Record<string, string> = {
  active: "border-transparent bg-emerald-50 text-emerald-700",
  inactive: "border-transparent bg-gray-50 text-gray-400",
  maintenance: "border-transparent bg-orange-50 text-orange-700",
};

const STATUSES = ["active", "inactive", "maintenance"] as const;

// devices.location — feeds waste_type derivation (POS2-630).

export default function SettingsDevicesPage() {
  const { locale, t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Device | null>(null);
  const [view, setView] = useState<"table" | "kanban">("table");
  const [sorting, setSorting] = useState<SortingState>([]);
  const contextSiteId = useAdminOrgStore((s) => s.siteId);

  const devicesQuery = useQuery<Device[]>({
    queryKey: ["devices"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/devices`, { headers });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status}: ${text || res.statusText}`);
      }
      return (await res.json()).data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });

  const kitchensQuery = useQuery<Kitchen[]>({
    queryKey: ["kitchens"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/kitchens`, { headers });
      if (!res.ok) return [];
      return (await res.json()).data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });

  const sitesQuery = useQuery<Site[]>({
    queryKey: ["sites"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/sites`, { headers });
      if (!res.ok) return [];
      return (await res.json()).data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });

  const accountsQuery = useQuery<Account[]>({
    queryKey: ["accounts"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/accounts`, { headers });
      if (!res.ok) return [];
      return (await res.json()).data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });

  const kitchens = useMemo(() => kitchensQuery.data ?? [], [kitchensQuery.data]);
  const sites = useMemo(() => sitesQuery.data ?? [], [sitesQuery.data]);
  const accounts = useMemo(() => accountsQuery.data ?? [], [accountsQuery.data]);

  const deviceDisplayName = useCallback(
    (device: Device) => {
      const kitchen = kitchens.find((k) => k.id === device.kitchen_id);
      const site = kitchen ? sites.find((s) => s.id === kitchen.site_id) : null;
      const acc = site ? accounts.find((a) => a.id === site.account_id) : null;
      const prefix =
        acc && site && kitchen
          ? `${acc.name}: ${site.name} - ${kitchen.name}`
          : (kitchen?.name ?? "");
      const label = device.name || device.device_serial;
      return prefix ? `${prefix} (${label})` : label;
    },
    [kitchens, sites, accounts],
  );

  const filtered = useMemo(() => {
    if (!devicesQuery.data) return [];
    let items = devicesQuery.data;
    if (contextSiteId) {
      const siteKitchenIds = new Set(
        kitchens.filter((k) => k.site_id === contextSiteId).map((k) => k.id),
      );
      items = items.filter((d) => siteKitchenIds.has(d.kitchen_id));
    }
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((d) => deviceDisplayName(d).toLowerCase().includes(q));
  }, [devicesQuery.data, search, deviceDisplayName, contextSiteId, kitchens]);

  const createMutation = useMutation({
    mutationFn: async (data: CreateDeviceForm) => {
      const res = await fetch(`${API_URL}/api/v1/devices`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          kitchen_id: data.kitchen_id,
          device_serial: data.device_serial,
          name: data.name || null,
        }),
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["devices"] });
      toast.success(t("settings.deviceRegistered"));
      setOpen(false);
    },
    onError: showApiError,
  });

  const editMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: {
        kitchen_id?: string;
        name?: string;
        location?: string | null;
      };
    }) => {
      const res = await fetch(`${API_URL}/api/v1/devices/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["devices"] });
      toast.success(t("settings.deviceUpdated"));
      setEditing(null);
    },
    onError: showApiError,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<CreateDeviceForm>({
    resolver: zodResolver(useMemo(() => makeCreateDeviceSchema(t), [t])),
  });

  const columns = useMemo<ColumnDef<Device>[]>(
    () => [
      {
        id: "displayName",
        accessorFn: (row) => deviceDisplayName(row),
        header: ({ column }) => (
          <span
            role="button"
            tabIndex={0}
            className="inline-flex cursor-pointer select-none items-center gap-1"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ")
                column.toggleSorting(column.getIsSorted() === "asc");
            }}
          >
            {t("settings.name")}
            <ArrowUpDown className="size-3 text-muted-foreground/50" />
          </span>
        ),
        cell: ({ row }) => (
          <span className="text-sm font-medium">{deviceDisplayName(row.original)}</span>
        ),
      },
      {
        accessorKey: "device_serial",
        header: "Serial",
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.device_serial}
          </span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <Badge
            className={
              STATUS_STYLES[row.original.status] ?? "border-transparent bg-gray-100 text-gray-600"
            }
          >
            {row.original.status}
          </Badge>
        ),
      },
      {
        accessorKey: "location",
        header: "Location",
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {optionLabel(t, DEVICE_LOCATION_OPTIONS, row.original.location) || "—"}
          </span>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Created",
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {formatDate(locale, row.original.created_at)}
          </span>
        ),
      },
      {
        id: "actions",
        cell: ({ row }) => (
          <Button
            size="icon"
            variant="ghost"
            className="size-7"
            aria-label={t("settings.editDeviceAria")}
            onClick={() => setEditing(row.original)}
          >
            <Pencil className="size-3.5" />
          </Button>
        ),
      },
    ],
    [deviceDisplayName, locale, t],
  );

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: PAGE_SIZE } },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-medium">{t("settings.devicesTitle")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("settings.devicesDescription")}</p>
        </div>
        <Dialog
          open={open}
          onOpenChange={(v) => {
            setOpen(v);
            if (!v) reset();
          }}
        >
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="mr-1 size-4" />
              {t("settings.newDevice")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("settings.registerDevice")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit((d) => createMutation.mutate(d))} className="space-y-4">
              <div>
                <label className="text-sm font-medium" htmlFor="kitchen_id">
                  {t("settings.kitchen")}
                </label>
                <select
                  id="kitchen_id"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                  {...register("kitchen_id")}
                >
                  <option value="">{t("settings.selectKitchen")}</option>
                  {kitchens.map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.name}
                    </option>
                  ))}
                </select>
                {errors.kitchen_id && (
                  <p className="mt-1 text-xs text-destructive">{errors.kitchen_id.message}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium" htmlFor="device_serial">
                    {t("settings.serialNumber")}
                  </label>
                  <Input
                    id="device_serial"
                    placeholder={t("settings.deviceSerialPlaceholder")}
                    {...register("device_serial")}
                  />
                  {errors.device_serial && (
                    <p className="mt-1 text-xs text-destructive">{errors.device_serial.message}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium" htmlFor="name">
                    {t("settings.nameOptional")}
                  </label>
                  <Input
                    id="name"
                    placeholder={t("settings.deviceNamePlaceholder")}
                    {...register("name")}
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={!isValid || createMutation.isPending}
              >
                {createMutation.isPending
                  ? t("settings.registering")
                  : t("settings.registerDevice")}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {devicesQuery.data && devicesQuery.data.length > 0 && (
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t("settings.searchDevices")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center rounded-md border border-gray-200">
            <Button
              size="sm"
              variant={view === "table" ? "secondary" : "ghost"}
              className="h-8 rounded-r-none px-2.5"
              onClick={() => setView("table")}
            >
              <Table2 className="size-4" />
            </Button>
            <Button
              size="sm"
              variant={view === "kanban" ? "secondary" : "ghost"}
              className="h-8 rounded-l-none px-2.5"
              onClick={() => setView("kanban")}
            >
              <Columns3 className="size-4" />
            </Button>
          </div>
        </div>
      )}

      {devicesQuery.isLoading && (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-10 w-full rounded" />
          ))}
        </div>
      )}

      {devicesQuery.isError && (
        <div className="rounded border border-destructive/50 bg-destructive/10 p-3">
          <p className="text-sm font-medium text-destructive">{t("settings.devicesLoadFailed")}</p>
          <p className="mt-1 font-mono text-xs text-destructive/80">
            {devicesQuery.error instanceof Error
              ? devicesQuery.error.message
              : t("settings.unknownError")}
          </p>
        </div>
      )}

      {devicesQuery.data && devicesQuery.data.length === 0 && (
        <div className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
          <Cpu className="size-10 text-gray-300" />
          <p className="text-sm">{t("settings.noDevices")}</p>
          <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
            {t("settings.registerFirstDevice")}
          </Button>
        </div>
      )}

      {filtered.length > 0 && view === "table" && (
        <>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((hg) => (
                <TableRow key={hg.id}>
                  {hg.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {table.getPageCount() > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-xs text-muted-foreground">
                {t("settings.pageOf", {
                  page: table.getState().pagination.pageIndex + 1,
                  total: table.getPageCount(),
                })}
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="size-8"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                >
                  <ChevronLeft className="size-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="size-8"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                >
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {filtered.length > 0 && view === "kanban" && (
        <KanbanBoard
          statuses={STATUSES}
          statusStyles={STATUS_STYLES}
          items={filtered}
          getStatus={(d) => d.status}
          getId={(d) => d.id}
          // Status is Positive Carbon's to set, on /admin/devices (POS2-1354): the board only shows it.
          onStatusChange={null}
          renderCard={(device) => (
            <>
              <p className="text-sm font-medium">{deviceDisplayName(device)}</p>
              <p className="mt-1 font-mono text-xs text-muted-foreground">{device.device_serial}</p>
              <div className="mt-2 flex items-center justify-end">
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-6"
                  aria-label={t("settings.editDeviceAria")}
                  onClick={() => setEditing(device)}
                >
                  <Pencil className="size-3" />
                </Button>
              </div>
            </>
          )}
        />
      )}

      <EditDeviceDialog
        device={editing}
        kitchens={kitchens}
        onClose={() => setEditing(null)}
        onSave={(data) => {
          if (!editing) return;
          editMutation.mutate({
            id: editing.id,
            data: {
              kitchen_id: data.kitchen_id || undefined,
              name: data.name || undefined,
              location: data.location || null,
            },
          });
        }}
        isPending={editMutation.isPending}
      />

      {search && filtered.length === 0 && devicesQuery.data && devicesQuery.data.length > 0 && (
        <div className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
          <p className="text-sm">{t("settings.noResultsFor", { search })}</p>
          <Button size="sm" variant="outline" onClick={() => setSearch("")}>
            {t("settings.clearSearch")}
          </Button>
        </div>
      )}
    </div>
  );
}

function EditDeviceDialog({
  device,
  kitchens,
  onClose,
  onSave,
  isPending,
}: {
  device: Device | null;
  kitchens: Kitchen[];
  onClose: () => void;
  onSave: (data: { kitchen_id?: string; name?: string; location?: string }) => void;
  isPending: boolean;
}) {
  const { t } = useTranslation();
  const editSchema = z.object({
    kitchen_id: z.string().min(1),
    name: z.string().optional(),
    location: z.enum(DEVICE_LOCATION_VALUES).or(z.literal("")),
  });
  type EditForm = z.infer<typeof editSchema>;

  const form = useForm<EditForm>({ resolver: zodResolver(editSchema) });
  // A device moves only between kitchens of its own site (POS2-1442); another site is
  // Positive Carbon's move, on the admin Devices page.
  const siteId = kitchens.find((k) => k.id === device?.kitchen_id)?.site_id;
  const siteKitchens = kitchens.filter((k) => k.site_id === siteId);

  const [prevId, setPrevId] = useState<string | null>(null);
  if (device && device.id !== prevId) {
    setPrevId(device.id);
    form.reset({
      kitchen_id: device.kitchen_id,
      name: device.name ?? "",
      location: (device.location ?? "") as EditForm["location"],
    });
  }

  return (
    <Dialog open={!!device} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("settings.editDevice")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSave)} className="space-y-4">
          <div>
            <label className="text-sm font-medium" htmlFor="edit-kitchen">
              {t("settings.kitchen")}
            </label>
            <select
              id="edit-kitchen"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
              {...form.register("kitchen_id")}
            >
              {siteKitchens.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.name}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium" htmlFor="edit-serial">
                {t("settings.serialNumber")}
              </label>
              {/* The device's identity (POS2-1389): new hardware takes it over through SERIAL_ID. */}
              <Input id="edit-serial" value={device?.device_serial ?? ""} readOnly disabled />
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="edit-name">
                {t("settings.name")}
              </label>
              <Input id="edit-name" {...form.register("name")} />
            </div>
          </div>
          <div>
            <div>
              <label className="text-sm font-medium" htmlFor="edit-location">
                {t("settings.location")}
              </label>
              <select
                id="edit-location"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                {...form.register("location")}
              >
                <option value="">{t("settings.none")}</option>
                {configOptions(t, DEVICE_LOCATION_OPTIONS).map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <Button type="submit" className="w-full" disabled={isPending}>
            {isPending ? t("common.saving") : t("settings.saveChangesButton")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
